import React from 'react';
import { Button } from '@/components/shared/button';
import { Share2 } from 'lucide-react';
import { Weights, OpportunityScore } from '@/types/opportunity';
import { useShareAssessment } from '@/hooks/useShareAssessment';
import { useToast } from '@/hooks/use-toast';

interface ShareAssessmentButtonProps {
  weights: Weights;
  scoredOpportunities: OpportunityScore[];
}

const ShareAssessmentButton = ({ weights, scoredOpportunities }: ShareAssessmentButtonProps) => {
  const { handleShare, isSharing } = useShareAssessment(weights, scoredOpportunities);
  const { toast } = useToast();

  const onShare = async () => {
    try {
      const shareUrl = await handleShare();
      console.log('Generated share URL:', shareUrl);
      
      await navigator.clipboard.writeText(shareUrl);
      
      toast({
        title: "Assessment Shared Successfully",
        description: (
          <div className="mt-2 flex flex-col gap-2">
            <div className="flex items-center gap-2 text-sm bg-green-50 p-2 rounded-md">
              <span className="text-green-600">✓</span>
              <span>Link copied to clipboard</span>
            </div>
            <div className="p-3 bg-slate-50 rounded-md border border-slate-200">
              <a 
                href={shareUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:underline break-all"
              >
                {shareUrl}
              </a>
            </div>
          </div>
        ),
        duration: 10000,
      });
    } catch (error) {
      console.error('Error sharing assessment:', error);
      toast({
        title: "Error",
        description: "Failed to generate share link. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Button
      variant="outline"
      onClick={onShare}
      className="gap-2"
      disabled={isSharing}
    >
      <Share2 className="w-4 h-4" />
      {isSharing ? 'Sharing...' : 'Share'}
    </Button>
  );
};

export default ShareAssessmentButton;